import {
  Box,
  ButtonBase,
  Chip, Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import HoverExplanationTooltip from '../../../../../../../../../../Common/HoverExplanationTooltip';
import {
  ComplianceChecklistItemFieldsFragment,
  ComplianceChecklistItemStatus,
  SessionSummaryFieldsFragment,
  useGetComplianceChecklistQuery,
  useListComplianceModulesQuery,
} from '../../../../../../../../../../generated/core-api/graphql-react-query';
import TopicTag from '../../../../../CommandPage/Components/CommandItem/Components/TopicTag/TopicTag';
import useModuleItems from '../../../../../CompliancePage/SubPages/MandatoryComplianceSubPage/Components/Hooks/useGroupByModule';
import withSessionDetailsSectionErrorBoundary from '../SessionDetailsSectionErrorBoundary';
import ComplianceTopicDetailsDialog from './ComplianceTopicDetailsDialog';
import ComplianceTag from './ComplianceTopicDetailsDialog/Components/ComplianceTag';
import StyledBox from './styles';

interface SessionComplianceProps {
  session: SessionSummaryFieldsFragment;
}

function EventItem(props: {
  event: ComplianceChecklistItemFieldsFragment,
  setComplianceDetails: React.Dispatch<React.SetStateAction<{
    text: string;
    color: string;
    textLower: string;
    status: ComplianceChecklistItemStatus;
  } | null>>,
  status: ComplianceChecklistItemStatus
}) {
  const { event, setComplianceDetails, status } = props;
  return (
    <ButtonBase key={event.topicTextLower} onClick={() => setComplianceDetails({ ...event.mandatoryTopic.topic, status: event.status })}>
      <ComplianceTag status={status} />
      <TopicTag topic={event.mandatoryTopic.topic.text} color={event.mandatoryTopic.topic.color} />
    </ButtonBase>
  );
}

function SessionCompliance(props: SessionComplianceProps) {
  const { session } = props;
  const { t } = useTranslation('session');
  const [complianceDetails, setComplianceDetails] = React.useState<{ text: string, color: string, textLower: string, status: ComplianceChecklistItemStatus } | null>(null);
  const complianceModules = useListComplianceModulesQuery();
  const complianceChecklist = useGetComplianceChecklistQuery({ input: { id: session.id } });
  const complianceChecklistItems = complianceChecklist.data?.getSession.complianceChecklist.items || [];
  const groupedByModule = useModuleItems(complianceModules.data?.complianceModule.list || [], complianceChecklistItems, (event) => event.topicTextLower);
  const hasAnyModule = React.useMemo(
    () => groupedByModule.some((group) => !!group.module),
    [groupedByModule],
  );

  if (!complianceChecklistItems) {
    return null;
  }
  return (
    <StyledBox>
      <Box className="header">
        <Typography variant="subtitle1">
          {t('details.compliance.title')}
        </Typography>
        <Chip
          label={t(`details.compliance.${session.finishedAt ? 'permanent' : 'temporary'}`)}
          size="small"
          avatar={(
            <HoverExplanationTooltip popoverId="compliance-temporary-version">
              {t(`details.compliance.${session.finishedAt ? 'permanent-tooltip' : 'temporary-tooltip'}`)}
            </HoverExplanationTooltip>
          )}
          color={session.finishedAt ? 'primary' : 'warning'}
        />
      </Box>
      {!hasAnyModule ? (
        <Box className="compliance-content">
          {complianceChecklistItems.map((event) => (
            <EventItem
              key={event.topicTextLower}
              event={event}
              setComplianceDetails={setComplianceDetails}
              status={event.status}
            />
          ))}
        </Box>
      ) : (
        <Box className="compliance-module-topics">
          {groupedByModule.map(({ module, items }) => {
            if (!module) {
              return (
                <Box className="mandatory-topics" key="without-module">
                  <Typography variant="h6">
                    {t('details.compliance.without-module')}
                  </Typography>
                  <Box className="compliance-content">
                    {items.map((item) => (
                      <EventItem
                        key={item.topicTextLower}
                        event={item}
                        setComplianceDetails={setComplianceDetails}
                        status={item.status}
                      />
                    ))}
                  </Box>
                </Box>
              );
            }
            return (
              <Box className="mandatory-topics" key={module.id}>
                <Box className="header">
                  <Typography variant="h6">
                    {module.name}
                  </Typography>
                  <Typography variant="subtitle2">
                    {`(${items.filter((e) => e.status).length}/${items.length})`}
                  </Typography>
                </Box>
                <Box className="compliance-content">
                  {items.map((event) => (
                    <EventItem
                      key={event.topicTextLower}
                      event={event}
                      setComplianceDetails={setComplianceDetails}
                      status={event.status}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {complianceDetails && (
        <ComplianceTopicDetailsDialog
          open={complianceDetails}
          session={session}
          status={complianceDetails.status}
          onClose={() => setComplianceDetails(null)}
        />
      )}
    </StyledBox>
  );
}

export default withSessionDetailsSectionErrorBoundary(SessionCompliance, 'session compliance');
